/**
 * Clipboard utilities for handling file paste operations
 *
 * Provides functions for extracting files from clipboard events
 * and integrating with the existing file upload system.
 */

import { logDebug, logError } from './safe-logger';
import { validateFile } from './file-upload';

export type ClipboardProcessResult = {
  files: File[];
  hasText: boolean;
  textContent?: string;
  errors: string[];
};

/**
 * Extract files and text content from a clipboard paste event
 */
export const processClipboardData = async (
  event: ClipboardEvent
): Promise<ClipboardProcessResult> => {
  const result: ClipboardProcessResult = {
    files: [],
    hasText: false,
    textContent: '',
    errors: [],
  };

  try {
    const clipboardData = event.clipboardData;
    if (!clipboardData) {
      result.errors.push('No clipboard data available');
      return result;
    }

    logDebug('Processing clipboard data', {
      types: Array.from(clipboardData.types),
      itemCount: clipboardData.items.length,
    });

    // Check for text content first
    if (clipboardData.types.includes('text/plain')) {
      const textContent = clipboardData.getData('text/plain');
      if (textContent && textContent.trim()) {
        result.hasText = true;
        result.textContent = textContent;
      }
    }

    // Check for files
    const files: File[] = [];

    // Process clipboard items
    for (let i = 0; i < clipboardData.items.length; i++) {
      const item = clipboardData.items[i];

      if (item?.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          logDebug('Found file in clipboard', {
            name: file.name,
            type: file.type,
            size: file.size,
          });

          // Generate a meaningful filename for pasted files if needed
          let processedFile = file;

          // If the file doesn't have a name (common with pasted images), generate one
          if (!file.name || file.name === 'image.png' || file.name === 'blob') {
            const timestamp = new Date()
              .toISOString()
              .replace(/\.\d+Z$/, '')
              .replace(/[:]/g, '-');
            const extension = getFileExtensionFromMimeType(file.type);
            const newName = `pasted-${timestamp}${extension}`;

            // Create a new File object with the generated name
            processedFile = new File([file], newName, {
              type: file.type,
              lastModified: file.lastModified,
            });

            logDebug('Generated filename for pasted file', {
              originalName: file.name,
              newName: processedFile.name,
            });
          }

          // Validate the file
          const validation = validateFile(processedFile);
          if (validation.valid) {
            files.push(processedFile);
          } else {
            result.errors.push(`Invalid file "${processedFile.name}": ${validation.error}`);
          }
        }
      }
    }

    // Also check the files property as a fallback
    if (files.length === 0 && clipboardData.files && clipboardData.files.length > 0) {
      const clipboardFiles = Array.from(clipboardData.files);
      for (const file of clipboardFiles) {
        const validation = validateFile(file);
        if (validation.valid) {
          files.push(file);
        } else {
          result.errors.push(`Invalid file "${file.name}": ${validation.error}`);
        }
      }
    }

    result.files = files;

    logDebug('Clipboard processing complete', {
      fileCount: result.files.length,
      hasText: result.hasText,
      errorCount: result.errors.length,
    });

    return result;
  } catch (error) {
    logError('Failed to process clipboard data', error);
    result.errors.push(error instanceof Error ? error.message : 'Failed to process clipboard data');
    return result;
  }
};

/**
 * Get appropriate file extension from MIME type
 */
const getFileExtensionFromMimeType = (mimeType: string): string => {
  const mimeToExt: Record<string, string> = {
    'image/png': '.png',
    'image/jpeg': '.jpg',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/bmp': '.bmp',
    'image/svg+xml': '.svg',
    'application/pdf': '.pdf',
    'text/plain': '.txt',
    'application/json': '.json',
    'text/html': '.html',
    'text/css': '.css',
    'application/javascript': '.js',
    'text/csv': '.csv',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  };

  return mimeToExt[mimeType] || '.bin';
};

/**
 * Check if the current clipboard contains files
 * This is useful for showing paste indicators in the UI
 */
export const checkClipboardForFiles = async (): Promise<boolean> => {
  try {
    // This requires the Clipboard API which may not be available in all contexts
    if (!navigator.clipboard || !navigator.clipboard.read) {
      return false;
    }

    const clipboardItems = await navigator.clipboard.read();
    return clipboardItems.some(item =>
      item.types.some(type => type.startsWith('image/') || type === 'application/pdf')
    );
  } catch (error) {
    // Clipboard API access failed (likely due to permissions or browser support)
    logDebug('Cannot check clipboard contents', error);
    return false;
  }
};

/**
 * Extract text content from clipboard event (for regular text pasting)
 */
export const getClipboardText = (event: ClipboardEvent): string => {
  const clipboardData = event.clipboardData;
  if (!clipboardData) return '';

  return clipboardData.getData('text/plain') || '';
};
